import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './GlobeView.css';

const GlobeView = () => {
  const navigate = useNavigate();
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [volandoA, setVolandoA] = useState(null);
  const viewerRef = useRef(null);

  useEffect(() => {
    api.get('/api/centros')
      .then(res => {
        if (res.data.success) setCentros(res.data.centros || []);
      })
      .catch(() => setCentros([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading || !document.getElementById('cesiumContainer')) return;

    let creditObserver = null;
    const loadCesium = () => {
      if (window.Cesium) {
        initCesium();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://cesium.com/downloads/cesiumjs/releases/1.104/Build/Cesium/Cesium.js';
      script.crossOrigin = 'anonymous';
      script.onload = () => { try { initCesium(); } catch (e) { console.error('GlobeView: initCesium', e); setLoading(false); } };
      script.onerror = () => { console.error('GlobeView: no se pudo cargar Cesium'); setLoading(false); };
      document.head.appendChild(script);
    };

    const initCesium = () => {
      try {
        if (!window.Cesium) {
          console.error('GlobeView: Cesium no está disponible');
          setLoading(false);
          return;
        }
      const Cesium = window.Cesium;
      const viewer = new Cesium.Viewer('cesiumContainer', {
        homeButton: true,
        baseLayerPicker: true,
        geocoder: false,
        animation: false,
        timeline: false,
        fullscreenButton: true,
        sceneModePicker: false,
        infoBox: false,
        imageryProvider: new Cesium.UrlTemplateImageryProvider({
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        }),
      });

      viewerRef.current = viewer;

      const ocultarStyle = 'display:none!important;visibility:hidden!important;height:0!important;max-height:0!important;overflow:hidden!important;opacity:0!important;position:absolute!important;left:-9999px!important;top:-9999px!important;';
      function ocultarCreditos() {
        try {
          if (viewer.cesiumWidget) {
            const w = viewer.cesiumWidget;
            if (w._creditContainer) w._creditContainer.style.cssText = ocultarStyle;
            if (w.creditContainer) w.creditContainer.style.cssText = ocultarStyle;
          }
          if (viewer.creditDisplay?.container) viewer.creditDisplay.container.style.cssText = ocultarStyle;
          if (viewer.bottomContainer) viewer.bottomContainer.style.cssText = ocultarStyle;
          const container = document.getElementById('cesiumContainer');
          if (container) {
            container.querySelectorAll('[class*="credit"], [class*="Credit"], .cesium-viewer-bottom, .cesium-widget-credits, a[href*="cesium"], a[href*="esri"], a[href*="arcgis"]').forEach(el => {
              el.style.cssText = ocultarStyle;
            });
          }
          document.querySelectorAll('.cesium-credit, .cesium-credit-box, .cesium-credit-expand-link, .cesium-credit-textContainer, .cesium-credit-logoContainer').forEach(el => {
            el.style.cssText = ocultarStyle;
          });
          document.querySelectorAll('[class*="credit"], [class*="Credit"]').forEach(el => {
            if (el.closest('#cesiumContainer') || el.closest('.cesium-viewer')) el.style.cssText = ocultarStyle;
          });
        } catch (e) {}
      }
      ocultarCreditos();
      [50, 100, 300, 500, 1000, 2000].forEach(t => setTimeout(ocultarCreditos, t));
      creditObserver = new MutationObserver(() => ocultarCreditos());
      try {
        const cc = document.getElementById('cesiumContainer');
        if (cc) creditObserver.observe(cc, { childList: true, subtree: true });
      } catch (_) {}
      try {
        viewer.scene.globe.showGroundAtmosphere = true;
      } catch (e) {}
      try {
        viewer.scene.globe.atmosphereLightIntensity = 15;
      } catch (e) {}
      try {
        viewer.scene.globe.atmosphereBrightnessShift = 0.15;
      } catch (e) {}
      try {
        viewer.scene.globe.atmosphereSaturationShift = 0.2;
      } catch (e) {}
      try {
        viewer.scene.globe.maximumScreenSpaceError = 0.5;
      } catch (e) {}
      try {
        viewer.scene.globe.tileCacheSize = 1000;
      } catch (e) {}

      // España: lon -10..5, lat 35..44. Si no está en España, intercambiar; si sigue fuera, forzar Madrid.
      const MADRID_LON = -3.69;
      const MADRID_LAT = 40.42;
      const enEspana = (lon, lat) => (lon >= -10 && lon <= 5 && lat >= 35 && lat <= 44);
      const corregirCoords = (lon, lat) => {
        if (Number.isNaN(lon) || Number.isNaN(lat)) return { lon: MADRID_LON, lat: MADRID_LAT };
        if (enEspana(lon, lat)) return { lon, lat };
        if (enEspana(lat, lon)) return { lon: lat, lat: lon };
        return { lon: MADRID_LON, lat: MADRID_LAT };
      };
      centros.forEach((c) => {
        let lon = parseFloat(c.lng);
        let lat = parseFloat(c.lat);
        const { lon: l, lat: la } = corregirCoords(lon, lat);
        lon = l;
        lat = la;
        const ciudad = (c.ciudad || '').trim();
        viewer.entities.add({
          id: c.id,
          position: Cesium.Cartesian3.fromDegrees(lon, lat, 500),
          point: {
            pixelSize: 20,
            color: Cesium.Color.CYAN,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 2,
          },
          label: ciudad ? {
            text: ciudad,
            font: 'bold 20px sans-serif',
            fillColor: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 4,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -14),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new Cesium.NearFarScalar(500000, 1.4, 8e6, 0.6),
          } : undefined,
        });
      });

      let animando = false;
      let creditInterval = null;
      
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((movement) => {
        try {
        // Prevenir múltiples animaciones simultáneas
        if (animando) {
          console.log('⏸️ Animación en curso, ignorando clic');
          return;
        }
        
        const picked = viewer.scene.pick(movement.position);
        if (Cesium.defined(picked) && picked.id) {
          const id = picked.id.id;
          const centro = centros.find((c) => c.id === id);
          if (centro) {
            let lon = parseFloat(centro.lng);
            let lat = parseFloat(centro.lat);
            const corregido = corregirCoords(lon, lat);
            lon = corregido.lon;
            lat = corregido.lat;
            animando = true;
            setVolandoA({ nombre: (centro.nombre || '').trim() });
            viewer.camera.cancelFlight();

            creditInterval = setInterval(ocultarCreditos, 100);

            // Fase 1: entrar al globo desde la vista actual hasta encima de la ciudad. Fase 2: bajada recta a la calle.
            // Llegada a altura de calle (2 m) para poder “caminar” y entrar al centro.
            const ALTURA_CIUDAD_M = 12000;
            const ALTURA_CALLE_M = 2;
            const ellipsoid = viewer.scene.globe.ellipsoid;
            const carto = ellipsoid.cartesianToCartographic(viewer.camera.position);
            const alturaActual = carto.height;

            viewer.camera.flyTo({
              destination: Cesium.Cartesian3.fromDegrees(lon, lat, ALTURA_CIUDAD_M),
              orientation: {
                heading: 0,
                pitch: Cesium.Math.toRadians(-90),
                roll: 0
              },
              duration: 5,
              maximumHeight: alturaActual,
              easingFunction: Cesium.EasingFunction.CUBIC_IN_OUT,
              complete: () => {
                setTimeout(() => {
                  const ciudad = (centro.ciudad || centro.nombre || '').trim();
                  if (ciudad) {
                    try {
                      viewer.entities.add({
                        position: Cesium.Cartesian3.fromDegrees(lon, lat, 0),
                        label: {
                          text: ciudad,
                          font: 'bold 36px sans-serif',
                          fillColor: Cesium.Color.WHITE,
                          outlineColor: Cesium.Color.BLACK,
                          outlineWidth: 4,
                          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                          pixelOffset: new Cesium.Cartesian2(0, -20),
                          disableDepthTestDistance: Number.POSITIVE_INFINITY,
                          scaleByDistance: new Cesium.NearFarScalar(500, 2, 8000, 0.5),
                        },
                      });
                    } catch (_) {}
                  }
                  try {
                    const labelsLayer = viewer.imageryLayers.addImageryProvider(
                      new Cesium.UrlTemplateImageryProvider({
                        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
                      }),
                      0
                    );
                    labelsLayer.alpha = 0.85;
                  } catch (e) {}
                }, 0);
                viewer.camera.flyTo({
                  destination: Cesium.Cartesian3.fromDegrees(lon, lat, ALTURA_CALLE_M),
                  orientation: {
                    heading: 0,
                    pitch: Cesium.Math.toRadians(-90),
                    roll: 0
                  },
                  duration: 3,
                  maximumHeight: ALTURA_CIUDAD_M,
                  easingFunction: Cesium.EasingFunction.CUBIC_IN_OUT,
                  complete: () => {
                    if (creditInterval) { clearInterval(creditInterval); creditInterval = null; }
                    ocultarCreditos();
                    animando = false;
                    setVolandoA(null);
                    const container = document.querySelector('.globe-container');
                    if (container) container.style.transition = 'opacity 0.2s';
                    if (container) container.style.opacity = '0';
                    document.body.style.background = '#111';
                    setTimeout(() => navigate(`/centro/${centro.id}`), 200);
                  }
                });
              }
            });
          }
        }
        } catch (err) {
          console.error('GlobeView: error en clic del globo', err);
          animando = false;
          if (creditInterval) { clearInterval(creditInterval); creditInterval = null; }
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      // Vista inicial: fijar cámara en España (Madrid). setView para que no lo pise nada.
      const RANGO_INICIAL = 25000000;
      const centrarEnEspana = () => {
        try {
          viewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(MADRID_LON, MADRID_LAT, RANGO_INICIAL),
            orientation: {
              heading: 0,
              pitch: Cesium.Math.toRadians(-90),
              roll: 0,
            },
          });
        } catch (err) {
          console.warn('GlobeView: error al centrar cámara', err);
        }
      };
      centrarEnEspana();
      setTimeout(centrarEnEspana, 600);
      setTimeout(centrarEnEspana, 1800);

      document.getElementById('loading-globe')?.remove();

      setTimeout(() => {
        try {
          viewer.scene.requestRender();
          viewer.resize();
        } catch (e) {
          console.warn('GlobeView: error resize', e);
        }
      }, 100);
    } catch (err) {
      console.error('GlobeView: error al inicializar Cesium', err);
      setLoading(false);
    }
    };

    loadCesium();

    return () => {
      creditObserver?.disconnect();
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [centros, loading]);

  if (loading) {
    return (
      <div className="globe-container">
        <div className="globe-loading" id="loading-globe">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="globe-container">
      <header className="globe-header">
        <h1>🌍 Multicentro Comercial Virtual</h1>
        <div className="globe-nav">
          <a href="/showcase">Directorio</a>
        </div>
      </header>

      <div id="cesiumContainer" className="cesium-container" />

      <div className="globe-instructions">
        Haz clic en un punto para volar hacia el centro comercial
      </div>

      {volandoA && (
        <div className="globe-volando-a">
          ✈️ Volando a: <strong>{volandoA.nombre}</strong>
        </div>
      )}

    </div>
  );
};

export default GlobeView;
